import { supabase } from '../lib/supabase';

/**
 * Goal Service - Data layer for managing user financial goals
 * 
 * This service provides CRUD operations for the goals table in Supabase.
 * All operations are automatically scoped to the authenticated user via RLS policies.
 * 
 * Database Schema:
 * - id: UUID primary key
 * - user_id: UUID foreign key to auth.users
 * - created_at, updated_at: Timestamps
 * - goal_name: TEXT - human readable goal name
 * - target_amount: DECIMAL - financial target amount
 * - time_horizon_years: INTEGER - years to achieve goal
 * - goal_details: JSONB - flexible storage for all other goal data
 */

/**
 * Creates a new financial goal for the authenticated user
 * 
 * @param {Object} goalData - The goal data from component state
 * @param {string} goalData.goalName - Name of the goal
 * @param {number} goalData.targetAmount - Target financial amount
 * @param {number} goalData.timeHorizon - Time horizon in years
 * @param {number} [goalData.startingCapital] - Initial investment amount
 * @param {number} [goalData.monthlyContribution] - Monthly contribution amount
 * @param {string} [goalData.selectedScenario] - Growth scenario (conservative, moderate, optimistic, custom)
 * @param {string} [goalData.projectionMode] - Projection mode (smooth, realistic)
 * @param {number} [goalData.customRate] - Custom growth rate percentage
 * @param {number} [goalData.volatilityFactor] - Volatility factor for realistic projections
 * @param {string} [goalData.strategy] - Goal strategy type
 * @param {string} [goalData.goalType] - Type of goal (general, retirement, etc.)
 * @param {Object} [goalData.results] - Calculation results object
 * @param {Array} [goalData.chartData] - Chart data for visualizations
 * @param {...*} [goalData.additionalProps] - Any other properties for goal_details
 * 
 * @returns {Promise<{data: Object|null, error: Error|null}>} 
 *   - data: The created goal object if successful
 *   - error: Error object if operation failed
 */
async function createGoal(goalData) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to save goals.') };
    }

    // Validate required fields
    if (!goalData.goalName || goalData.goalName.trim() === '') {
      return { data: null, error: new Error('Goal name is required') };
    }
    
    if (!goalData.targetAmount || goalData.targetAmount <= 0) {
      return { data: null, error: new Error('Target amount must be greater than 0') };
    }
    
    if (!goalData.timeHorizon || goalData.timeHorizon <= 0) {
      return { data: null, error: new Error('Time horizon must be greater than 0') };
    }

    // Map goalData to our hybrid database schema
    const { goalName, targetAmount, timeHorizon, ...additionalData } = goalData;
    
    // Prepare the goal record
    const goalRecord = {
      user_id: session.user.id,
      goal_name: goalName.trim(),
      target_amount: parseFloat(targetAmount),
      time_horizon_years: parseInt(timeHorizon),
      goal_details: {
        // Financial planning data
        starting_capital: parseFloat(additionalData.startingCapital) || 0,
        monthly_contribution: parseFloat(additionalData.monthlyContribution) || 0,
        
        // Scenario and projection settings
        selected_scenario: additionalData.selectedScenario || 'moderate',
        projection_mode: additionalData.projectionMode || 'smooth',
        custom_rate: parseFloat(additionalData.customRate) || 45,
        volatility_factor: parseFloat(additionalData.volatilityFactor) || 70,
        
        // Goal metadata
        strategy: additionalData.strategy || 'goal',
        goal_type: additionalData.goalType || 'general',
        
        // Calculation results and data
        results: additionalData.results || null,
        chart_data: additionalData.chartData || [],
        
        // Progress tracking
        current_progress: additionalData.currentProgress || 0,
        months_completed: additionalData.monthsCompleted || 0,
        total_months: additionalData.totalMonths || (parseInt(timeHorizon) * 12),
        
        // Any additional properties not explicitly handled above
        ...Object.fromEntries(
          Object.entries(additionalData).filter(([key]) => 
            !['startingCapital', 'monthlyContribution', 'selectedScenario', 'projectionMode', 
              'customRate', 'volatilityFactor', 'strategy', 'goalType', 'results', 'chartData',
              'currentProgress', 'monthsCompleted', 'totalMonths'].includes(key)
          )
        )
      }
    };

    // Insert the goal into the database
    const { data, error } = await supabase
      .from('goals')
      .insert([goalRecord])
      .select('*')
      .single();

    if (error) {
      console.error('Database error creating goal:', error);
      return { data: null, error: new Error(`Failed to create goal: ${error.message}`) };
    }

    return { data, error: null };

  } catch (error) {
    console.error('Unexpected error in createGoal:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Retrieves all goals for the authenticated user
 * 
 * @returns {Promise<{data: Array|null, error: Error|null}>}
 *   - data: Array of goal objects if successful, empty array if no goals
 *   - error: Error object if operation failed
 */
async function getGoals() {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to view goals.') };
    }

    // Query goals for the current user
    const { data, error } = await supabase
      .from('goals')
      .select('*')
      .eq('user_id', session.user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Database error fetching goals:', error);
      return { data: null, error: new Error(`Failed to fetch goals: ${error.message}`) };
    }

    // Return goals (data will be an empty array if no goals exist)
    return { data: data || [], error: null };

  } catch (error) {
    console.error('Unexpected error in getGoals:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Updates an existing goal by ID
 * 
 * @param {string} goalId - The UUID of the goal to update
 * @param {Object} updates - Object containing fields to update
 * @param {string} [updates.goal_name] - Updated goal name
 * @param {number} [updates.target_amount] - Updated target amount
 * @param {number} [updates.time_horizon_years] - Updated time horizon
 * @param {Object} [updates.goal_details] - Updated goal details (will be merged with existing)
 * @param {...*} [updates.additionalFields] - Any other fields to update
 * 
 * @returns {Promise<{data: Object|null, error: Error|null}>}
 *   - data: The updated goal object if successful
 *   - error: Error object if operation failed
 */
async function updateGoal(goalId, updates) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to update goals.') };
    }

    // Validate goalId
    if (!goalId || typeof goalId !== 'string') {
      return { data: null, error: new Error('Valid goal ID is required') };
    }

    // Validate updates object
    if (!updates || typeof updates !== 'object' || Object.keys(updates).length === 0) {
      return { data: null, error: new Error('Updates object is required and must not be empty') };
    }

    // If updating goal_details, we need to merge with existing data to avoid overwriting
    let finalUpdates = { ...updates };
    
    if (updates.goal_details) {
      // First fetch the current goal to get existing goal_details
      const { data: currentGoal, error: fetchError } = await supabase
        .from('goals')
        .select('goal_details')
        .eq('id', goalId)
        .eq('user_id', session.user.id)
        .single();

      if (fetchError) {
        console.error('Error fetching current goal for update:', fetchError);
        return { data: null, error: new Error(`Failed to fetch current goal: ${fetchError.message}`) };
      }

      // Merge existing goal_details with new updates
      finalUpdates.goal_details = {
        ...(currentGoal.goal_details || {}),
        ...updates.goal_details
      };
    }

    // Add updated_at timestamp
    finalUpdates.updated_at = new Date().toISOString();

    // Update the goal
    const { data, error } = await supabase
      .from('goals')
      .update(finalUpdates)
      .eq('id', goalId)
      .eq('user_id', session.user.id) // Extra security: ensure user owns this goal
      .select('*')
      .single();

    if (error) {
      console.error('Database error updating goal:', error);
      
      // Provide more specific error messages
      if (error.code === 'PGRST116') {
        return { data: null, error: new Error('Goal not found or you do not have permission to update it') };
      }
      
      return { data: null, error: new Error(`Failed to update goal: ${error.message}`) };
    }

    return { data, error: null };

  } catch (error) {
    console.error('Unexpected error in updateGoal:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Deletes a goal by ID
 * 
 * @param {string} goalId - The UUID of the goal to delete
 * 
 * @returns {Promise<{data: boolean|null, error: Error|null}>}
 *   - data: true if deletion was successful
 *   - error: Error object if operation failed
 */
async function deleteGoal(goalId) {
  try {
    // Get current user session
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      return { data: null, error: new Error(`Session error: ${sessionError.message}`) };
    }
    
    if (!session || !session.user) {
      return { data: null, error: new Error('User not authenticated. Please log in to delete goals.') };
    }

    // Validate goalId
    if (!goalId || typeof goalId !== 'string') {
      return { data: null, error: new Error('Valid goal ID is required') };
    }

    // Delete the goal
    const { error } = await supabase
      .from('goals')
      .delete()
      .eq('id', goalId)
      .eq('user_id', session.user.id); // Extra security: ensure user owns this goal

    if (error) {
      console.error('Database error deleting goal:', error);
      
      // Provide more specific error messages
      if (error.code === 'PGRST116') {
        return { data: null, error: new Error('Goal not found or you do not have permission to delete it') };
      }
      
      return { data: null, error: new Error(`Failed to delete goal: ${error.message}`) };
    }

    return { data: true, error: null };

  } catch (error) {
    console.error('Unexpected error in deleteGoal:', error);
    return { data: null, error: new Error(`Unexpected error: ${error.message}`) };
  }
}

/**
 * Goal Service Object
 * 
 * Provides a clean interface for all goal-related database operations.
 * All methods handle authentication, validation, and error handling automatically.
 */
const goalService = {
  createGoal,
  getGoals,
  updateGoal,
  deleteGoal
};

export default goalService;